import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useTranslation } from 'react-i18next';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(t('auth.passwordMismatch', 'Passwörter stimmen nicht überein.'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.passwordTooShort', 'Passwort muss mindestens 8 Zeichen haben.'));
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', { token, new_password: password });
      navigate('/login', { state: { resetSuccess: true } });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('auth.resetError', 'Token ungültig oder abgelaufen.'));
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="card w-full max-w-md text-center">
          <p className="text-red-600 font-medium mb-4">{t('auth.resetInvalidLink', 'Ungültiger Reset-Link.')}</p>
          <Link to="/forgot-password" className="btn btn-primary">
            {t('auth.forgotButton', 'Neuen Link anfordern')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md">
        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {t('auth.resetTitle', 'Neues Passwort setzen')}
          </h1>
          <p className="text-gray-500 text-sm mt-1 mb-6">
            {t('auth.resetSubtitle', 'Wähle ein sicheres Passwort mit mindestens 8 Zeichen.')}
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">{t('auth.newPassword', 'Neues Passwort')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="label">{t('auth.confirmPassword', 'Passwort bestätigen')}</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading || !password || !confirm}
              className="btn btn-primary w-full"
            >
              {loading ? t('auth.saving', 'Wird gespeichert…') : t('auth.resetButton', 'Passwort speichern')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
